import { getLogger } from "./util/logger";
import { PersistentConnection } from "./util/signalr-helpers";
import { GamePlanLoggingHub, IGamePlanLoggingHub } from "./util/signalr-logging-hub";
import { formatDate, logToDevSqlite } from "./dev-logging";
import { createLogTransport } from "./log-transport";

interface LoggingSetup {
    loggingKey: string;
    loggingId: string;
}

const setup: LoggingSetup = {
    loggingId: "",
    loggingKey: "",
};

// Token renewal and the pagehide drain both live in log-transport.ts, which is
// import-safe outside a browser — this module is not, because of the $()
// bootstrap at the bottom.
const transport = createLogTransport();

/** The page's session id, or '' on a page the shell did not render. */
function gpSessionId(): string {
    return typeof __gameplan_session_id === 'string' ? __gameplan_session_id : '';
}

let logToConsole = false;
let logToServer = true;
// In local dev, logs are also mirrored to the DevServer's SQLite sink so
// the terminal can `sqlite3 gameplan-debug.db ...` to tail — that flag
// (logToDevSqlite) and recordDevLog live in dev-logging.ts, which is
// import-safe outside a browser (this module's $() bootstrap is not).

if (typeof (__gameplan_is_local_development) !== 'undefined' && __gameplan_is_local_development) {
    logToConsole = true;
    logToServer = false;
}

export function configureLogs() {
    // look for the special logging setup command
    if (typeof (__gpLogs) !== "undefined" && __gpLogs && __gpLogs.length > 0) {
        const logsToSend = __gpLogs;

        __gpLogs = [];

        logsToSend.forEach(parts => {
            let when: Date = parts[0];
            let msgTemplate: string = parts[1];

            if (msgTemplate === "gpLogSetup") {
                const setupArgs: LoggingSetup = parts[2];

                setup.loggingId = setupArgs.loggingId;
                setup.loggingKey = setupArgs.loggingKey;
                transport.seedToken(setupArgs.loggingKey);

            } else {
                // if it's not a setup command, put the message back for posting.
                __gpLogs.push(parts);
            }
        });
    }
}

let signalrConnection: PersistentConnection | null = null;
let loggingHub: IGamePlanLoggingHub | null;
function gameplanSignalrConnection() {

    if (!signalrConnection) {

        signalrConnection = new PersistentConnection('logging', '', (conn) => {
            loggingHub = new GamePlanLoggingHub(conn);
        })
    }

    return signalrConnection;
}

export function sendLogs() {
    if (!(typeof (__gpLogs) !== "undefined" && __gpLogs && __gpLogs.length > 0)) {
        return;
    }
    const logsToSend = __gpLogs;
    __gpLogs = [];

    if (logToConsole) {
        logsToSend.forEach(parts => {
            const when: Date = parts[0];
            const msgTemplate: string = parts[1];
            const args: any[] = parts[2];
            const extras: any = parts[3];
            getLogger('general').debug(msgTemplate, args, extras ? JSON.stringify(extras, null, '\t') : "");
        });
    }

    if (logToServer) {

        const connection = gameplanSignalrConnection();

        connection.ensureConnection()
            .done(() => {
                let hub = loggingHub;

                logsToSend.forEach(parts => {
                    if (!hub) return;
                    const when: Date = parts[0];
                    const msgTemplate: string = parts[1];
                    const args: any[] = parts[2];
                    const extras: any = parts[3];

                    // Extras first, then the page-derived values on top, so a
                    // call site cannot shadow them. Same precedence the server
                    // enforces for ClientConnectionId and UserEmail
                    // (ClientLogWriter.Write), applied at the other end of the
                    // wire: an extras `sessionId` winning here would key hub
                    // rows on the call site's value while beacon rows carried
                    // the real one, splitting one session in two. No call site
                    // sets either key today — this keeps it that way by
                    // construction rather than by grep.
                    const props: any = {};

                    if (extras) {
                        for (let k in extras) {
                            props[k] = extras[k];
                        }
                    }

                    // UTC, so this column reads the same whichever path
                    // delivered the row and sorts against App Insights' own
                    // `timestamp` with no offset arithmetic. formatDate stays
                    // local-with-offset for the dev SQLite sink further down,
                    // where a human tailing a log wants their own clock.
                    props.clientTime = when.toISOString();
                    props.sessionId = gpSessionId();

                    hub.information(transport.token() || setup.loggingKey, msgTemplate, args, props);
                });
            });
    }

    if (logToDevSqlite) {
        // Fire-and-forget POST to the DevServer's SQLite sink.
        // Failures don't surface — the console mirror above is the
        // dev's primary feedback channel.
        const entries = logsToSend.map(parts => {
            const when: Date = parts[0];
            const msgTemplate: string = parts[1];
            const args: any[] = parts[2];
            const extras: any = parts[3];
            // Extras often carries a "ClientContext" field that names the
            // module/feature (see jsloggly-service.ts) — promote it to
            // the category column. Plan id, if present in extras, lifts
            // out into its own column for cheap filtering.
            const category = extras?.ClientContext ?? extras?.category ?? null;
            const planId = extras?.planId ?? extras?.PlanId ?? null;
            const message = renderTemplate(msgTemplate, args);
            const payload = extras ? JSON.stringify(extras) : null;
            return {
                sourceTime: formatDate(when),
                source: 'browser',
                category,
                planId,
                level: 'info',
                message,
                payloadJson: payload,
            };
        });
        try {
            fetch('/api/dev-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ entries }),
                // No credentials needed — endpoint is unauthenticated in dev.
            }).catch(() => { /* swallow */ });
        } catch { /* swallow */ }
    }
}

/**
 * Trivial {0}/{1} index substitution + {Name} keyed substitution against
 * the args array. Mirrors how `getLogger().information(msg, args)`
 * displays them in the console. Not a full Serilog templater — best-
 * effort dev-time rendering.
 */
function renderTemplate(template: string, args: any[]): string {
    if (!args || args.length === 0) return template;
    let i = 0;
    return template.replace(/\{(\w+)\}/g, (_match, _name) => {
        const v = args[i++];
        if (v === undefined) return _match;
        try { return typeof v === 'string' ? v : JSON.stringify(v); }
        catch { return String(v); }
    });
}

/**
 * Delivers what is queued right now through the unload beacon.
 *
 * For a caller that logs something as the page goes away. The drain registered
 * in the bootstrap below hooks `pagehide` at jQuery ready (and only under
 * `logToServer`), and listeners fire in registration order — so anything logged by a listener registered later (when a
 * dialog opened, say) lands in an array the drain has already emptied and never
 * leaves the browser. Calling this straight after that log is what delivers it.
 *
 * Safe to call when nothing is queued: the transport returns without sending.
 */
export function gpDrainLogsOnDeparture(): void {
    if (!logToServer) {
        return;
    }

    transport.flushNow(true);
}

$(() => {

    function sendLogsAndSetTimer() {
        sendLogs();
        setTimeout(sendLogsAndSetTimer, 1000);
    };

    configureLogs();

    if (logToServer) {
        // Renewal, because the attestation token minted into this page lives
        // five minutes and the home app is hash-routed — signing up never
        // re-renders, so without this an anonymous session goes deaf mid-funnel.
        transport.start();

        // The last-gasp drain. The pump above cannot deliver what is queued when
        // the page hides: it waits on a SignalR handshake that will not complete.
        transport.hookUnload(
            () => {
                const pending = (typeof __gpLogs !== "undefined" && __gpLogs) ? __gpLogs : [];
                __gpLogs = [];
                return pending;
            },
            gpSessionId);
    }

    setTimeout(sendLogsAndSetTimer, 1000);

});
